import {useCallback} from "react"
import gql from "graphql-tag"
import {pick} from "lodash"
import {MutationUpdateBudgetCategoriesArgs, UpdateBudgetCategoriesInput} from "@irati/types"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {createUseMutation, UseMutationExtractor} from "@irati/utils/services/apollo"
import {useBudgetsPermissions} from "./useBudgetsPermissions"
import {useBudgetsList} from "./useBudgetsList"

const useBudgetsUpdateMutation = createUseMutation<void, MutationUpdateBudgetCategoriesArgs>(gql`
  mutation UpdateBudgetCategories($input: UpdateBudgetCategoriesInput!) {
    updateBudgetCategories(input: $input)
  }
`)

type Extracted = UseMutationExtractor<typeof useBudgetsUpdateMutation>

export type UpdateBugets = Pick<UpdateBudgetCategoriesInput, "categories" | "newCategories">

type UseBudgetsUpdate = {
  update: (updates: UpdateBugets) => Promise<Extracted["mutationResult"]>
}

export function useBudgetsUpdate(): UseBudgetsUpdate {
  const {
    user: {uuid}
  } = useAuth({})
  const {canBudget} = useBudgetsPermissions()
  const {data, refetch} = useBudgetsList()
  const [updateBudgets] = useBudgetsUpdateMutation({
    onCompleted: refetch
  })

  const categories = data?.budgets?.categories

  const update = useCallback(
    async (updates: UpdateBugets) => {
      if (!canBudget || !categories) return
      return updateBudgets({
        variables: {
          input: {
            userId: uuid!,
            categories: updates.categories.map((c) => pick(c, ["amount", "color", "name", "tagId", "uuid"])),
            newCategories: updates.newCategories.map((c) => pick(c, ["amount", "color", "name", "tagId", "uuid"]))
          }
        }
      }).then(refetch)
    },
    [updateBudgets, refetch, canBudget, categories, uuid]
  )

  return {update}
}
